import React, { useEffect, useState } from 'react';
import '@/styles/ekuba.css';
import Header from '@/components/ekuba/Header';
import Jukebox from '@/components/ekuba/Jukebox';
import Hero from '@/components/ekuba/Hero';
import HeroSlideshow from '@/components/ekuba/HeroSlideshow';
import LatestReleases from '@/components/ekuba/LatestReleases';
import FeaturedTracks from '@/components/ekuba/FeaturedTracks';
import About from '@/components/ekuba/About';
import ListenEverywhere from '@/components/ekuba/ListenEverywhere';
import Footer from '@/components/ekuba/Footer';
import ContactSection from '@/components/ekuba/ContactSection';
import FeaturedVideos from '@/components/ekuba/FeaturedVideos';
import NewsletterSection from '@/components/ekuba/NewsletterSection';
import CookieBanner from '@/components/ekuba/CookieBanner';
import BackToTop from '@/components/ekuba/BackToTop';
import InstallPrompt from '@/components/ekuba/InstallPrompt';
import PushOptIn from '@/components/ekuba/PushOptIn';
import { Toaster as SonnerToaster } from 'sonner';
import { PlayerModalProvider, usePlayerModal } from '@/components/ekuba/EmbedModal';
import { ApiRelease } from '@/hooks/useSiteContent';
import { useNotice } from '@/hooks/useNotice';
import { useDesignZoom } from '@/hooks/useDesignZoom';
import { NoticeProvider } from '@/hooks/useNotice';
import { useSiteContent } from '@/hooks/useSiteContent';
import { NavId, NAV } from '@/config/site';
import { mediaUrl } from '@/lib/api';
import { HEADER_OFFSET } from '@/lib/scroll';

/**
 * Highlights the nav item of the section currently under the sticky header.
 * Sections are evaluated in DOM order (not NAV order — e.g. Releases sits above Music
 * and Contact above About), and the probe line sits just under the header so the
 * highlight only moves once a section's title has actually reached the top.
 */
function useScrollSpy(): NavId {
  const [active, setActive] = useState<NavId>('home');
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const header = document.querySelector('.ek-header') as HTMLElement | null;
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      const probe = Math.max(HEADER_OFFSET, headerBottom) + 56;
      const sections = NAV.map((n) => ({ id: n.id, el: document.getElementById(n.target) }))
        .filter((s): s is { id: NavId; el: HTMLElement } => !!s.el)
        .map((s) => ({ id: s.id, top: s.el.getBoundingClientRect().top }))
        .sort((a, b) => a.top - b.top);
      let current: NavId = 'home';
      for (const s of sections) {
        if (s.top <= probe) current = s.id;
      }
      // At the very bottom the last section may never reach the probe line.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4 && sections.length) {
        const visible = sections.filter((s) => s.top < window.innerHeight);
        const last = visible[visible.length - 1];
        if (last && last.top > probe) current = last.id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return active;
}

/** Opens /release/<slug> deep links once live content has loaded. */
function ReleaseDeepLink({ releases, loaded }: { releases: ApiRelease[]; loaded: boolean }) {
  const { openRelease } = usePlayerModal();
  const { notify } = useNotice();
  const done = React.useRef(false);
  useEffect(() => {
    if (!loaded || done.current) return;
    const m = /^\/release\/([^/?#]+)/.exec(window.location.pathname);
    if (!m) return;
    done.current = true;
    const slug = decodeURIComponent(m[1]).toLowerCase();
    const r = releases.find((x) => (x.slug || '').toLowerCase() === slug);
    if (r) openRelease(r, { force: true });
    else {
      window.history.replaceState(window.history.state, '', '/');
      notify('That release is no longer available.');
    }
  }, [loaded, releases, openRelease, notify]);
  return null;
}

export default function Landing() {
  const { zoom, desktop } = useDesignZoom();
  const active = useScrollSpy();
  const { content, loaded } = useSiteContent();
  const s = content.settings;

  // Prefer uploaded hero images over the bundled placeholder; the first upload
  // becomes the static background (via CSS var), and 2+ images turn into a
  // crossfade slideshow rendered on top.
  const heroImages = s.hero.image_urls || s.hero.images || [];
  const firstHero = heroImages[0];
  const heroBgPath = firstHero
    ? (firstHero.url || `/api/files/${firstHero.file_id}`)
    : '/assets/hero/hero-full.jpg';

  return (
    <NoticeProvider>
      <PlayerModalProvider allReleases={content.all_releases}>
        <ReleaseDeepLink releases={content.all_releases} loaded={loaded} />
        <div className="ek-viewport">
          <div
            className={`ek-canvas ${desktop ? 'is-desktop' : 'is-fluid'}`}
            style={
              {
                ...(desktop ? { zoom } : {}),
                ['--ek-img-hero' as string]: `url(${mediaUrl(heroBgPath)})`,
                ['--ek-img-about' as string]: `url(${mediaUrl('/assets/about/about-panorama.jpg')})`,
              } as React.CSSProperties
            }
            data-testid="ekuba-canvas"
          >
            <div id="home" className="ek-top">
              {heroImages.length >= 2 && (
                <HeroSlideshow images={heroImages} intervalMs={s.hero.slideshow_interval_ms} />
              )}
              <Header active={active} socials={s.socials} />
              <Hero hero={s.hero} />
            </div>
            <Jukebox />
            <main>
              <LatestReleases releases={content.releases} allReleasesUrl={s.links.all_releases_url} />
              <FeaturedTracks tracks={content.tracks} spotifyUrl={s.links.spotify_artist_url} />
              <FeaturedVideos videos={content.videos || []} loaded={loaded} />
              <ContactSection />
              <About about={s.about} />
              <ListenEverywhere platforms={s.platforms} socials={s.socials} />
              <NewsletterSection />
            </main>
            <Footer copyright={s.footer.copyright} contactEmail={s.links.contact_email} />
          </div>
        </div>
        <CookieBanner />
        <BackToTop />
        <InstallPrompt />
        <PushOptIn />
        <SonnerToaster
          position="bottom-right"
          theme="dark"
          richColors={false}
          closeButton
          duration={6000}
          toastOptions={{
            style: {
              background: 'rgba(10, 13, 20, 0.96)',
              border: '1px solid rgba(223, 94, 184, 0.3)',
              color: '#fefefe',
              boxShadow: '0 10px 40px rgba(0,0,0,0.55)',
              backdropFilter: 'blur(12px) saturate(140%)',
            },
          }}
        />
      </PlayerModalProvider>
    </NoticeProvider>
  );
}
